import { getSelfServicePathPrefix } from '@/helper/selfServicePath';

export const GLOBAL_POPUP_EVENTS = {
  LOGOUT: 'swd:logout',
  ORDER_ABANDON: 'swd:order-abandon',
  CANCELLATION_FORM: 'swd:cancellation-form',
} as const;

export type GlobalPopupEventName = (typeof GLOBAL_POPUP_EVENTS)[keyof typeof GLOBAL_POPUP_EVENTS];

/**
 * Marks an intentional logout so `/logout/` can mirror DY "Logout Popups"
 * (URL alone is not enough — direct visits should not trigger).
 *
 * Only covers Next.js logout buttons. Self-service (Angular) logout never sets
 * this; that path is detected via `hasSelfServiceLogoutReferrer()`.
 */
export const PENDING_LOGOUT_POPUP_STORAGE_KEY = 'swd:pending-logout-popup';

/**
 * Marks a successfully submitted cancellation form so the popup can fire on its
 * thank-you page (`targetPage`), where the form itself is no longer mounted.
 */
export const PENDING_CANCELLATION_POPUP_STORAGE_KEY = 'swd:pending-cancellation-popup';

export function dispatchGlobalPopupEvent(name: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event(name));
}

/** True for the post-logout page `/logout`. */
export function isLogoutPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return normalized === '/logout';
}

/** Call on logout button click, before redirecting to the logout flow. */
export function markPendingLogoutPopup(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(PENDING_LOGOUT_POPUP_STORAGE_KEY, '1');
  } catch {
    // private mode / blocked storage — popup simply won't trigger
  }
}

/** Returns true once if a logout-button flow marked the pending flag, then clears it. */
export function consumePendingLogoutPopup(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const pending = window.sessionStorage.getItem(PENDING_LOGOUT_POPUP_STORAGE_KEY) === '1';
    if (pending) {
      window.sessionStorage.removeItem(PENDING_LOGOUT_POPUP_STORAGE_KEY);
    }
    return pending;
  } catch {
    return false;
  }
}

/** Call when a cancellation form submission succeeds, before navigating to the thank-you page. */
export function markPendingCancellationPopup(): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(PENDING_CANCELLATION_POPUP_STORAGE_KEY, '1');
  } catch {
    // private mode / blocked storage — popup simply won't trigger
  }
}

/** Returns true once after a cancellation form submission, then clears the flag. */
export function consumePendingCancellationPopup(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const pending = window.sessionStorage.getItem(PENDING_CANCELLATION_POPUP_STORAGE_KEY) === '1';
    if (pending) {
      window.sessionStorage.removeItem(PENDING_CANCELLATION_POPUP_STORAGE_KEY);
    }
    return pending;
  } catch {
    return false;
  }
}

/**
 * True when `/logout/` was reached from the customer portal (Angular),
 * e.g. dashboard → logout → full navigation to `/logout/`.
 */
export function hasSelfServiceLogoutReferrer(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const { referrer } = document;
    if (!referrer) return false;

    const refUrl = new URL(referrer);
    if (refUrl.origin !== window.location.origin) return false;

    const prefix = getSelfServicePathPrefix();
    const path = refUrl.pathname.replace(/\/+$/, '') || '/';
    return path === prefix || path.startsWith(`${prefix}/`);
  } catch {
    return false;
  }
}

/** Intentional logout: Next.js button flag, or navigation from self-service. */
export function shouldDispatchLogoutPopup(): boolean {
  return consumePendingLogoutPopup() || hasSelfServiceLogoutReferrer();
}
